import type { FieldType, SigningField } from '../types/fields';
import { clampFieldToPage } from './coordinates';
import { normalizeRotation } from './pageRotation';

/**
 * Field coordinates are stored and validated in VISUAL space (the coordinate
 * system the user actually sees and drags in, which matches pdf.js's
 * page.getViewport()).
 *
 * The server mirrors this contract:
 *   server/src/utils/signingValidation.ts converts visual→media at the
 *   boundary, after clamping in visual space.
 *
 * Therefore every helper that returns "the box I should clamp this field
 * against" must return the visual dimensions — NOT the media dimensions
 * that come from page.getSize() — for any page with a non-zero /Rotate.
 */

export interface PageSizeWithRotation {
  width: number;
  height: number;
  rotation?: number;
  /** True when width/height are already pdf.js viewport (visual) dimensions. */
  visual?: boolean;
}

export type PageSize = { width: number; height: number };

/**
 * Convert a server-provided page entry (media + optional rotation) into the
 * VISUAL dimensions for that page.
 *
 * Visual = media when rotation is 0/180; swapped when rotation is 90/270.
 * Exported because several callers (not just clamp sites) need to know the
 * visible canvas footprint — e.g. default field sizes and RightInspector
 * layout math.
 */
export function getVisualPageSize(page: PageSizeWithRotation): PageSize {
  if (page.visual) {
    return { width: page.width, height: page.height };
  }
  const r = normalizeRotation(page.rotation ?? 0);
  if (r === 90 || r === 270) {
    return { width: page.height, height: page.width };
  }
  return { width: page.width, height: page.height };
}

/**
 * Inverse of getVisualPageSize — recover media-box dimensions from a session
 * pageSizes entry (server upload metadata or pdf.js viewport after sync).
 */
export function getMediaDimensionsFromPageEntry(page: PageSizeWithRotation): PageSize {
  if (!page.visual) {
    return { width: page.width, height: page.height };
  }
  const r = normalizeRotation(page.rotation ?? 0);
  if (r === 90 || r === 270) {
    return { width: page.height, height: page.width };
  }
  return { width: page.width, height: page.height };
}

/** Stable media metadata for usePdfDocument seeding (immune to visual sync swaps). */
export function toMediaPageSizesForSeed(
  pageSizes: ReadonlyArray<PageSizeWithRotation>
): Array<{ width: number; height: number; rotation?: number }> {
  return pageSizes.map((p) => ({
    ...getMediaDimensionsFromPageEntry(p),
    rotation: p.rotation,
  }));
}

/**
 * Get the visual dimensions of the page a field belongs to.
 * Returns null if the field references a page that doesn't exist (instead of
 * silently falling back to Letter, which used to mask bugs and produce
 * fields clamped to a misleading 612×792 box).
 */
export function getPageSizeForField(
  field: Pick<SigningField, 'pageIndex'>,
  pageSizes: ReadonlyArray<PageSizeWithRotation>
): PageSize | null {
  const page = pageSizes[field.pageIndex];
  if (!isValidPageSize(page)) return null;
  return getVisualPageSize(page);
}

/**
 * True if the supplied page-size entry is a valid positive box.
 * Used as a guard before we trust the rotation/width/height values.
 */
function isValidPageSize(p: PageSizeWithRotation | undefined): p is PageSizeWithRotation {
  return (
    !!p &&
    Number.isFinite(p.width) &&
    Number.isFinite(p.height) &&
    p.width > 0 &&
    p.height > 0
  );
}

/**
 * True when any of the geometry coordinates is NaN/Infinity.
 * Drag/resize code computes pdfX/pdfY via division by effectiveScaleX/Y —
 * a zero or NaN scale (e.g. transient layout) would otherwise propagate
 * NaN through the field, into history, and out to the server (which then
 * 400s the whole sign request with an opaque "invalid x" error).
 */
function hasInvalidGeometry(updates: Partial<SigningField>): boolean {
  for (const k of ['x', 'y', 'width', 'height'] as const) {
    const v = updates[k];
    if (v === undefined) continue;
    if (typeof v !== 'number' || !Number.isFinite(v)) return true;
  }
  return false;
}

/**
 * Apply a partial update to a field, clamping the result to the VISUAL
 * bounds of the page it lives on. If the field is missing the page
 * (out-of-range pageIndex, empty pageSizes, or a page with a non-finite
 * size), the field is returned unchanged. NaN/Infinity updates are dropped
 * outright so they never enter history or the server payload.
 *
 * Returns a new object on success, or the original reference when no
 * meaningful change can be made (callers can rely on reference equality
 * to short-circuit work).
 */
export function applyFieldUpdate(
  field: SigningField,
  updates: Partial<SigningField>,
  pageSizes: ReadonlyArray<PageSizeWithRotation>
): SigningField {
  if (hasInvalidGeometry(updates)) {
    // Drop poisoned updates rather than corrupt history and force a 400 from the server.
    return field;
  }
  const merged = { ...field, ...updates };
  const page = pageSizes[merged.pageIndex];
  if (!isValidPageSize(page)) return field;
  const visual = getVisualPageSize(page);
  return clampFieldToPage(merged, visual.width, visual.height);
}

export function isSignedDownloadStale(hasSignedDownload: boolean, fieldsChanged: boolean): boolean {
  return hasSignedDownload && fieldsChanged;
}

const SNAPSHOT_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  'signature-draw',
  'signature-upload',
  'signature-type',
  'date',
  'name',
  'text',
  'initials',
  'checkbox',
  'image',
  'highlight',
  'underline',
  'strikeout',
  'rect',
  'ellipse',
  'cross',
  'text-bubble',
  'ink',
]);

/** Basic schema check before restoring snapshot/template fields. */
export function isValidSnapshotField(entry: unknown): entry is SigningField {
  if (!entry || typeof entry !== 'object') return false;
  const f = entry as Record<string, unknown>;
  if (typeof f.id !== 'string' || !f.id.trim()) return false;
  if (typeof f.type !== 'string' || !SNAPSHOT_FIELD_TYPES.has(f.type as FieldType)) return false;
  if (typeof f.pageIndex !== 'number' || !Number.isInteger(f.pageIndex) || f.pageIndex < 0) {
    return false;
  }
  let width = 0;
  let height = 0;
  for (const k of ['x', 'y', 'width', 'height'] as const) {
    const v = f[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) return false;
    if (k === 'width') width = v;
    if (k === 'height') height = v;
  }
  if (width <= 0 || height <= 0) return false;
  return true;
}
